import { useQuery, UseQueryResult } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import {
  AssociationType,
  fileAssociationType,
} from "@/shared/domain/association";

import { fileQueryKeys } from "./fileQueryKeys";

const IGNORED_PAGE_SIZE = 10;

/**
 * How many items a directory listing holds, for its pager. With a
 * `searchTerm`, counts the matching files of any association through file
 * search. Without one, only a dataset folder can be counted, so an org,
 * device or device type folder resolves to `null` without a request.
 */
export function useDirectoryItemCount(
  directoryPath: string,
  associationId: string,
  resourceOwnerId?: string,
  searchTerm?: string,
  extensions?: string[],
  showHiddenFiles: boolean = false,
): UseQueryResult<number | null, Error> {
  const { files: fileService } = useDomainServices();

  return useQuery({
    queryKey: fileQueryKeys.directoryItemCount({
      directoryPath,
      associationId,
      searchTerm,
      extensions,
      showHiddenFiles,
    }),
    queryFn: async ({ signal }) => {
      if (searchTerm) {
        return fileService.getFileCountForDirectory({
          path: directoryPath,
          associationId,
          fileNameSearchTerm: searchTerm,
          extensions,
          showHiddenFiles,
          pageSize: IGNORED_PAGE_SIZE,
          options: {
            abortSignal: signal,
            resourceOwnerId,
          },
        });
      }

      if (fileAssociationType(associationId) !== AssociationType.Dataset) {
        return null;
      }

      return fileService.getItemCountForDirectory({
        directoryPath,
        datasetId: associationId,
        extensions,
        showHiddenFiles,
        options: {
          abortSignal: signal,
          resourceOwnerId,
        },
      });
    },
    placeholderData: (previousData) => previousData,
  });
}
